import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import MatchRoster from "./MatchRoster";
import MatchBulletin from "./MatchBulletin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Check if BACKEND_URL already contains /api to avoid duplication
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const MatchReport = () => {
  const { matchId } = useParams();
  const [match, setMatch] = useState(null);
  const [report, setReport] = useState(null);
  const [selectedView, setSelectedView] = useState("summary"); // summary, details, aggregates, roster, bulletin
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isAdmin } = useAuth();
  const [matchYear, setMatchYear] = useState(null);
  const admin = typeof isAdmin === "function" ? isAdmin() : !!isAdmin;

  useEffect(() => {
    const fetchMatchData = async () => {
      try {
        // Get the token from localStorage
        const token = localStorage.getItem('token');
        if (!token) {
          setError("Authentication required. Please log in again.");
          setLoading(false);
          return;
        }

        // Set authorization header
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        };
        
        // Fetch match details
        const matchResponse = await axios.get(`${API}/matches/${matchId}`, config);
        setMatch(matchResponse.data);
        
        // Set match year
        const matchDate = new Date(matchResponse.data.date);
        setMatchYear(matchDate.getFullYear());
        
        // Fetch match report
        const reportResponse = await axios.get(`${API}/match-report/${matchId}`, config);

        // Normalize the score map to one canonical key per score: `${instance_name}_${caliber}`.
        // The backend keys shooterData.scores off a Python str-enum repr (e.g.
        // "NMC1_CaliberType.TWENTYTWO"), whose exact form varies by data vintage. Re-key every
        // score off its own explicit fields (match_type_instance + caliber value) so render can
        // do exact-key lookups instead of a fragile multi-candidate/substring heuristic.
        if (reportResponse.data && reportResponse.data.shooters) {
          Object.values(reportResponse.data.shooters).forEach((shooterData) => {
            if (!shooterData.scores) return;
            const normalizedScores = {};
            Object.values(shooterData.scores).forEach((scoreData) => {
              const canonicalKey = `${scoreData.score.match_type_instance}_${scoreData.score.caliber}`;
              normalizedScores[canonicalKey] = scoreData;
            });
            shooterData.scores = normalizedScores;
          });
        }

        setReport(reportResponse.data);

        // Calculate aggregates if they're missing
        if (reportResponse.data && reportResponse.data.shooters) {
          Object.entries(reportResponse.data.shooters).forEach(([shooterId, shooterData]) => {
            // Check if this shooter has aggregates
            if (!shooterData.aggregates || Object.keys(shooterData.aggregates).length === 0) {
              // For 1800 (3x600) aggregate
              if (matchResponse.data.aggregate_type === "1800 (3x600)") {
                // Group scores by caliber
                const scoresByCaliberType = {};
                
                Object.entries(shooterData.scores).forEach(([key, scoreData]) => {
                  const caliber = scoreData.score.caliber;
                  if (!scoresByCaliberType[caliber]) {
                    scoresByCaliberType[caliber] = [];
                  }
                  scoresByCaliberType[caliber].push(scoreData.score);
                });
                
                // Calculate aggregate for each caliber with at least 3 scores
                const aggregates = {};
                Object.entries(scoresByCaliberType).forEach(([caliber, scores]) => {
                  if (scores.length >= 3) {
                    // Only include non-null scores in the totals
                    const totalScore = scores.reduce((sum, score) => 
                      score.total_score !== null ? sum + score.total_score : sum, 0);
                    const totalXCount = scores.reduce((sum, score) => 
                      score.total_x_count !== null ? sum + score.total_x_count : sum, 0);
                    
                    const caliberId = caliber === ".22" ? "TWENTYTWO" : 
                                     caliber === "CF" ? "CENTERFIRE" : 
                                     caliber === ".45" ? "FORTYFIVE" : caliber;
                    
                    aggregates[`1800_${caliberId}`] = {
                      score: totalScore,
                      x_count: totalXCount,
                      components: scores.map(s => s.match_type_instance)
                    };
                  }
                });
                
                if (Object.keys(aggregates).length > 0) {
                  shooterData.aggregates = aggregates;
                }
              }
            }
          });
        }
        
        setReport(reportResponse.data);
        
        setLoading(false);
      } catch (err) {
        console.error("Error fetching match details:", err);
        setError("Failed to load match details. Please try again.");
        setLoading(false);
      }
    };

    fetchMatchData();
  }, [matchId]);

  if (loading) return <div className="page text-center text-ink-600">Loading match details...</div>;
  if (error) return <div className="page text-center text-rose-600">{error}</div>;
  if (!match) return <div className="page text-center text-ink-600">Match not found</div>;

  const shooterCount = report && report.shooters ? Object.keys(report.shooters).length : 0;
  const scoreCount = report && report.shooters ? 
    Object.values(report.shooters).reduce((total, shooter) => 
      total + Object.keys(shooter.scores).length, 0) : 0;
  
  // Get unique calibers used in this match
  const calibers = new Set();
  if (report && report.shooters) {
    Object.values(report.shooters).forEach(shooter => {
      Object.values(shooter.scores).forEach(score => {
        calibers.add(score.score.caliber);
      });
    });
  }

  // Helper to determine the winner for each match type and caliber
  const getWinners = () => {
    if (!report || !report.shooters) return {};
    
    const winners = {};
    
    // Group scores by match type and caliber
    const matchTypeCaliberScores = {};
    
    Object.entries(report.shooters).forEach(([shooterId, shooterData]) => {
      Object.entries(shooterData.scores).forEach(([key, scoreData]) => {
        const [instance, caliber] = key.split('_');
        
        if (!matchTypeCaliberScores[instance]) {
          matchTypeCaliberScores[instance] = {};
        }
        
        if (!matchTypeCaliberScores[instance][caliber]) {
          matchTypeCaliberScores[instance][caliber] = [];
        }
        
        matchTypeCaliberScores[instance][caliber].push({
          shooterId,
          shooterName: shooterData.shooter.name,
          score: scoreData.score.total_score,
          xCount: scoreData.score.total_x_count
        });
      });
    });
    
    // Find winner for each category
    Object.entries(matchTypeCaliberScores).forEach(([instance, caliberData]) => {
      Object.entries(caliberData).forEach(([caliber, scores]) => {
        // Sort by score (highest first), then by X count (highest first)
        scores.sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          return b.xCount - a.xCount;
        });
        
        if (scores.length > 0) {
          const key = `${instance}_${caliber}`;
          winners[key] = scores[0];
        }
      });
    });
    
    return winners;
  };

  const winners = getWinners();

  // Helper function to format caliber for display
  const formatCaliber = (caliber) => {
    if (caliber === "TWENTYTWO" || caliber === ".22") return ".22";
    if (caliber === "CENTERFIRE" || caliber === "CF") return "CF";
    if (caliber === "FORTYFIVE" || caliber === ".45") return ".45";
    return caliber;
  };

  return (
    <div className="page">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between no-print">
        <div>
          <div className="flex items-center mb-2">
            <h1 className="page-title">{match.name}</h1>
            {matchYear && (
              <span className="ml-3 badge-gray">
                {matchYear}
              </span>
            )}
          </div>
          <div className="text-ink-600 space-y-1">
            <p><span className="font-medium">Date:</span> {new Date(match.date).toLocaleDateString()}</p>
            <p><span className="font-medium">Location:</span> {match.location}</p>
            {match.aggregate_type !== "None" && (
              <p>
                <span className="font-medium">Aggregate Type:</span>
                <span className="ml-2 badge-brand">
                  {match.aggregate_type}
                </span>
              </p>
            )}
          </div>
        </div>
        
        <div className="mt-4 md:mt-0 flex flex-col space-y-2">
          <Link to={`/matches`} className="btn-secondary">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
            </svg>
            Back to Matches
          </Link>
          
          {/* Excel Download Button */}
          <button 
            onClick={async () => {
              try {
                const token = localStorage.getItem('token');
                if (!token) {
                  alert("Authentication required. Please log in again.");
                  return;
                }
                
                // Create headers with authentication
                const headers = {
                  Authorization: `Bearer ${token}`
                };
                
                // Perform authenticated request with proper response handling
                const response = await axios.get(`${API}/match-report/${matchId}/excel`, {
                  headers,
                  responseType: 'blob' // Important for binary data like Excel files
                });
                
                // Create a download link
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `match_report_${match.name.replace(/\s+/g, '_')}_${new Date(match.date).toISOString().split('T')[0]}.xlsx`);
                document.body.appendChild(link);
                link.click();
                
                // Clean up
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
              } catch (err) {
                console.error("Error downloading Excel report:", err);
                alert("Failed to download Excel report. Please try again.");
              }
            }}
            className="btn-primary"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
            </svg>
            Download Excel Report
          </button>
          
          {/* Edit Match Button for Admins */}
          {admin && (
            <>
              <Link
                to={`/matches/${matchId}/edit`}
                className="btn-secondary"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                </svg>
                Edit Match
              </Link>

              {/* Add Scores Button */}
              <Link
                to={`/scores/add/${matchId}`}
                className="btn-primary mt-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                Add Scores
              </Link>
            </>
          )}
        </div>
      </div>
      
      {/* Match Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="card card-pad">
          <h3 className="section-title mb-2">Participants</h3>
          <p className="text-3xl font-bold text-ink-900">{shooterCount}</p>
          <p className="text-ink-500 text-sm mt-1">shooters participated</p>
        </div>

        <div className="card card-pad">
          <h3 className="section-title mb-2">Scores</h3>
          <p className="text-3xl font-bold text-ink-900">{scoreCount}</p>
          <p className="text-ink-500 text-sm mt-1">total scores recorded</p>
        </div>

        <div className="card card-pad">
          <h3 className="section-title mb-2">Calibers</h3>
          <div className="flex flex-wrap gap-2 mt-2">
            {Array.from(calibers).map((caliber, idx) => (
              <span key={idx} className="badge-gray">
                {formatCaliber(caliber)}
              </span>
            ))}
            {calibers.size === 0 && <p className="text-ink-500">No calibers recorded</p>}
          </div>
        </div>
      </div>
      
      {/* View Selector */}
      <div className="mb-6 border-b border-ink-200 no-print">
        <div className="flex flex-wrap">
          <button
            onClick={() => setSelectedView("summary")}
            className={`px-4 py-2 font-medium text-sm ${
              selectedView === "summary"
                ? "border-b-2 border-brand-600 text-brand-600"
                : "text-ink-500 hover:text-ink-700"
            }`}
          >
            Summary
          </button>
          <button
            type="button"
            onClick={() => setSelectedView("roster")}
            className={`px-4 py-2 font-medium text-sm ${
              selectedView === "roster"
                ? "border-b-2 border-brand-600 text-brand-600"
                : "text-ink-500 hover:text-ink-700"
            }`}
          >
            Roster
          </button>
          <button
            onClick={() => setSelectedView("details")}
            className={`px-4 py-2 font-medium text-sm ${
              selectedView === "details"
                ? "border-b-2 border-brand-600 text-brand-600"
                : "text-ink-500 hover:text-ink-700"
            }`}
          >
            Detailed Scores
          </button>
          <button
            type="button"
            onClick={() => setSelectedView("bulletin")}
            className={`px-4 py-2 font-medium text-sm ${
              selectedView === "bulletin"
                ? "border-b-2 border-brand-600 text-brand-600"
                : "text-ink-500 hover:text-ink-700"
            }`}
          >
            Results Bulletin
          </button>
        </div>
      </div>
      
      {selectedView === "roster" && (
        <MatchRoster matchId={matchId} />
      )}

      {selectedView === "bulletin" && (
        <MatchBulletin matchId={matchId} matchName={match.name} />
      )}

      {selectedView === "summary" && (
        <>
          <div className="mb-8">
            <h2 className="text-2xl font-semibold mb-4 text-ink-900">Match Configuration</h2>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Instance</th>
                    <th>Type</th>
                    <th>Calibers</th>
                    <th className="text-right">Max Score</th>
                  </tr>
                </thead>
                <tbody>
                  {match.match_types.map((mt, index) => (
                    <tr key={index}>
                      <td className="whitespace-nowrap">
                        <div className="font-medium text-ink-900">{mt.instance_name}</div>
                      </td>
                      <td className="whitespace-nowrap">
                        <div className="text-ink-900">{mt.type}</div>
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {mt.calibers.map((caliber, idx) => (
                            <span key={idx} className="badge-gray">
                              {formatCaliber(caliber)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="whitespace-nowrap text-right font-medium">
                        {mt.type === "NMC" ? "300" :
                        mt.type === "600" ? "600" : 
                        mt.type === "900" ? "900" : 
                        mt.type === "Presidents" ? "400" : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Match Summary Table */}
          {report && report.shooters && Object.keys(report.shooters).length > 0 && (
            <div className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-ink-900">Match Summary</h2>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Shooter Name</th>
                      {match.match_types.map((mt) => (
                        mt.calibers.map((caliber) => (
                          <th key={`${mt.instance_name}_${caliber}`} className="text-center">
                            {mt.instance_name} ({formatCaliber(caliber)})
                          </th>
                        ))
                      ))}
                      {match.aggregate_type !== "None" && (
                        <th className="text-center">
                          {match.aggregate_type}
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(report.shooters).map(([shooterId, shooterData]) => {
                      // For aggregates
                      const aggregates = shooterData.aggregates || {};
                      
                      return (
                        <tr key={shooterId}>
                          <td className="whitespace-nowrap">
                            <Link to={`/shooters/${shooterId}`} className="font-medium text-brand-600 hover:underline">
                              {shooterData.shooter.name}
                            </Link>
                          </td>
                          
                          {/* Generate cells for each match type and caliber */}
                          {match.match_types.map((mt) => (
                            mt.calibers.map((caliber) => {
                              // Scores were normalized to a canonical `${instance_name}_${caliber}`
                              // key on receipt, so an exact lookup is both correct and unambiguous.
                              const cellKey = `${mt.instance_name}_${caliber}`;
                              const scoreData = shooterData.scores[cellKey] || null;

                              return (
                                <td key={cellKey} className="text-center">
                                  {scoreData ? (
                                    <div>
                                      <span className="font-medium text-ink-900">{scoreData.score.total_score === null ? "-" : scoreData.score.total_score}</span>
                                      {scoreData.score.total_score !== null && (
                                        <span className="text-ink-500 text-xs ml-1">({scoreData.score.total_x_count === null ? "0" : scoreData.score.total_x_count}X)</span>
                                      )}
                                      {scoreData.score.fired_twentytwo && caliber !== ".22" && (
                                        <span
                                          className="ml-1 inline-block rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-800 align-middle"
                                          title={`Fired a .22 for this ${formatCaliber(caliber)} card — scored in the "22 Only" group`}
                                        >
                                          .22
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-ink-400">-</span>
                                  )}
                                </td>
                              );
                            })
                          ))}
                          
                          {/* Aggregate Score */}
                          {match.aggregate_type !== "None" && (
                            <td className="text-center">
                              {(() => {
                                // For 1800 (3x600) or 1800 (2x900) match, we want to show the sum of all calibers
                                if (match.aggregate_type === "1800 (3x600)" || match.aggregate_type === "1800 (2x900)") {
                                  // Calculate grand total across all calibers
                                  let totalScore = 0;
                                  let totalXCount = 0;
                                  let hasScores = false;
                                  
                                  // Go through all scores and sum them up
                                  Object.entries(shooterData.scores).forEach(([key, scoreData]) => {
                                    // Only include non-null scores
                                    if (scoreData.score.total_score !== null) {
                                      totalScore += scoreData.score.total_score;
                                      totalXCount += scoreData.score.total_x_count;
                                      hasScores = true;
                                    }
                                  });
                                  
                                  if (hasScores) {
                                    return (
                                      <div className="font-medium text-ink-900">
                                        {totalScore}<span className="text-ink-500 text-xs ml-1">({totalXCount}X)</span>
                                      </div>
                                    );
                                  } else {
                                    return <span className="text-ink-400">-</span>;
                                  }
                                }
                                
                                // For other aggregate types, show individual aggregates
                                let aggregateScores = [];
                                
                                // If server-provided aggregates exist, use them
                                if (shooterData.aggregates && Object.keys(shooterData.aggregates).length > 0) {
                                  Object.entries(shooterData.aggregates).forEach(([aggKey, aggData]) => {
                                    aggregateScores.push(
                                      <div key={aggKey} className="font-medium text-ink-900">
                                        {aggData.score}<span className="text-ink-500 text-xs ml-1">({aggData.x_count}X)</span>
                                        <div className="text-ink-400 text-xs">{formatCaliber(aggKey.split('_')[1])}</div>
                                      </div>
                                    );
                                  });
                                }

                                return aggregateScores.length > 0 ?
                                  aggregateScores :
                                  <span className="text-ink-400">-</span>;
                              })()}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Winners Section */}
          {Object.keys(winners).length > 0 && (
            <div className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-ink-900">Category Winners</h2>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Shooter</th>
                      <th className="text-center">Score</th>
                      <th className="text-center">X Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(winners).map(([category, winner]) => {
                      const [matchType, caliber] = category.split('_');
                      return (
                        <tr key={category}>
                          <td className="whitespace-nowrap">
                            <div className="font-medium text-ink-900">{matchType}</div>
                            <div className="text-xs text-ink-500">{formatCaliber(caliber)}</div>
                          </td>
                          <td className="whitespace-nowrap">
                            <Link to={`/shooters/${winner.shooterId}`} className="font-medium text-brand-600 hover:underline">
                              {winner.shooterName}
                            </Link>
                          </td>
                          <td className="text-center font-medium text-ink-900">
                            {winner.score}
                          </td>
                          <td className="text-center">
                            {winner.xCount}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Detailed Scores */}
      {selectedView === "details" && (
        <div>
          <h2 className="text-2xl font-semibold mb-4 text-ink-900">Match Results</h2>
          {report && report.shooters && Object.keys(report.shooters).length > 0 ? (
            <div className="space-y-8">
              {Object.entries(report.shooters).map(([shooterId, shooterData]) => (
                <div key={shooterId} className="card overflow-hidden">
                  <div className="px-6 py-4 bg-ink-50 border-b border-ink-100">
                    <h3 className="section-title">
                      <Link to={`/shooters/${shooterId}`} className="text-brand-600 hover:underline">
                        {shooterData.shooter.name}
                      </Link>
                      {shooterData.shooter.nra_number && (
                        <span className="ml-2 text-sm font-normal text-ink-500">
                          NRA: {shooterData.shooter.nra_number}
                        </span>
                      )}
                    </h3>
                  </div>

                  <div className="px-6 py-4">
                    {/* Scorecard Table View */}
                    <div className="table-wrap mb-4">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Instance Name</th>
                            <th>Match Type</th>
                            <th>Caliber</th>
                            <th className="text-center">Total Score</th>
                            <th className="text-center">X Count</th>
                            <th className="text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(shooterData.scores).map(([key, scoreData]) => {
                            const [instance, caliber] = key.split('_');
                            const isWinner = winners[key] && winners[key].shooterId === shooterId;
                            
                            // Get match type to check if it's a 900 aggregate
                            const matchTypeInstance = match.match_types.find(mt => mt.instance_name === instance);
                            const is900 = matchTypeInstance && matchTypeInstance.type === "900";
                            
                            return (
                              <tr key={key} className={isWinner ? "bg-emerald-50" : ""}>
                                <td className="whitespace-nowrap">
                                  {instance}
                                </td>
                                <td className="whitespace-nowrap">
                                  {matchTypeInstance ? matchTypeInstance.type : "-"}
                                  {isWinner && (
                                    <span className="ml-2 badge-green">
                                      Winner
                                    </span>
                                  )}
                                </td>
                                <td className="whitespace-nowrap">
                                  {formatCaliber(caliber)}
                                </td>
                                <td className="text-center font-medium text-ink-900">
                                  {scoreData.score.total_score === null ? "-" : scoreData.score.total_score}
                                </td>
                                <td className="text-center">
                                  {scoreData.score.total_x_count === null ? "-" : scoreData.score.total_x_count}
                                </td>
                                <td className="text-center">
                                  <div className="flex justify-center space-x-3">
                                    <button
                                      onClick={() => {
                                        const detailElement = document.getElementById(`scorecard-${shooterId}-${key}`);
                                        if (detailElement) {
                                          detailElement.open = !detailElement.open;
                                        }
                                      }}
                                      className="text-brand-600 hover:text-brand-800 text-sm font-medium"
                                    >
                                      View
                                    </button>
                                    {admin && (
                                      <Link
                                        to={`/scores/edit/${scoreData.score.id}`}
                                        className="text-emerald-600 hover:text-emerald-800 text-sm font-medium"
                                      >
                                        Edit
                                      </Link>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Expandable Scorecards */}
                    <div className="mt-4 space-y-6">
                      {Object.entries(shooterData.scores).map(([key, scoreData]) => {
                        const [instance, caliber] = key.split('_');
                        const matchTypeInstance = match.match_types.find(mt => mt.instance_name === instance);
                        const matchType = matchTypeInstance ? matchTypeInstance.type : "Unknown";
                        const is900 = matchType === "900";
                        
                        return (
                          <details key={`scorecard-${key}`} id={`scorecard-${shooterId}-${key}`} className="bg-ink-50 rounded-lg p-4">
                            <summary className="font-medium cursor-pointer mb-3 text-ink-800">
                              {matchType} - {formatCaliber(caliber)} Scorecard
                            </summary>

                            <div className="mt-3 overflow-x-auto">
                              <table className="min-w-full divide-y divide-ink-100 text-sm">
                                <thead className="bg-ink-100">
                                  <tr>
                                    <th className="px-4 py-2 text-left font-medium text-ink-700">Stage</th>
                                    <th className="px-4 py-2 text-center font-medium text-ink-700">Score</th>
                                    <th className="px-4 py-2 text-center font-medium text-ink-700">X Count</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-ink-100">
                                  {/* Stage Scores */}
                                  {scoreData.score.stages.map((stage, idx) => (
                                    <tr key={`stage-${idx}`}>
                                      <td className="px-4 py-2 font-medium">{stage.name}</td>
                                      <td className="px-4 py-2 text-center">{stage.score === null ? "-" : stage.score}</td>
                                      <td className="px-4 py-2 text-center">{stage.x_count === null ? "-" : stage.x_count}</td>
                                    </tr>
                                  ))}

                                  {/* Subtotals for 900 match type */}
                                  {is900 && (
                                    <>
                                      <tr className="bg-ink-100">
                                        <td colSpan="3" className="px-4 py-2 font-medium text-ink-700">Subtotals</td>
                                      </tr>
                                      {Object.entries(scoreData.subtotals).map(([name, values], idx) => (
                                        <tr key={`subtotal-${idx}`} className="bg-brand-50">
                                          <td className="px-4 py-2 font-medium">{name}</td>
                                          <td className="px-4 py-2 text-center font-medium">{values.score === null ? "-" : values.score}</td>
                                          <td className="px-4 py-2 text-center">{values.x_count === null ? "-" : values.x_count}</td>
                                        </tr>
                                      ))}
                                    </>
                                  )}

                                  {/* Total Score */}
                                  <tr className="bg-ink-100 font-medium">
                                    <td className="px-4 py-2">Total</td>
                                    <td className="px-4 py-2 text-center">{scoreData.score.total_score === null ? "-" : scoreData.score.total_score}</td>
                                    <td className="px-4 py-2 text-center">{scoreData.score.total_x_count === null ? "-" : scoreData.score.total_x_count}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </details>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card card-pad text-center">
              <p className="text-ink-500 mb-4">No scores have been recorded for this match.</p>
              {admin && (
                <Link to={`/scores/add/${matchId}`} className="btn-primary">
                  Add First Score
                </Link>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MatchReport;